import { createContext, useState, useEffect, useContext } from "react";
import { retrieveTokens, validateTokenWithAPI } from "$api/tokenManagement";
import { login as loginFunc, logout as logoutFunc } from "$api/authentication";
import { notification } from "antd";
import { AxiosError } from "axios";
import { useNavigate } from "react-router";
import { configureClient } from "$api/client";

const defaultAuthContextValues = {
  isAuthenticated: false,
  isAdmin: false,
  bearerToken: "",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
};

export const AuthContext = createContext(defaultAuthContextValues);

const { token: accessToken } = retrieveTokens() || {};

const AuthContextProvider = (props) => {
  const [bearerToken, setBearerToken] = useState(accessToken || "");
  const [usedUsername, setUsedUsername] = useState();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  const handleApiLogout = () => {
    console.log("Invalid token detected, logging out...");
    logoutFunc(); // Clear localStorage
    setBearerToken("");
    setIsAdmin(false);
    setIsAuthenticated(false);
  };

  // Effect to sync with localStorage changes
  // Initial token validation on component mount
  useEffect(() => {
    const { token } = retrieveTokens();
    if (token && token !== '""' && token !== null && token !== "") {
      validateTokenWithAPI(token).then((isAdmin) => {
        if (isAdmin === null) {
          // Definitively invalid (401/403/422) — redirect to login
          setBearerToken("");
          setIsAdmin(false);
          setIsAuthenticated(false);
        } else if (isAdmin !== undefined) {
          // Valid token
          setIsAdmin(isAdmin);
          setIsAuthenticated(true);
        }
        // undefined = network/server error: keep current state, polling will retry
      });
    }
  }, []);

  useEffect(() => {
    const checkTokenStatus = () => {
      const { token } = retrieveTokens();
      // If token is empty or null, ensure state reflects that and clear localStorage
      if (
        !token ||
        token === "" ||
        token === "null" ||
        token === '""' ||
        token.length === 0
      ) {
        if (bearerToken !== "" && bearerToken !== '""') {
          handleApiLogout();
        }
      } else if (token !== bearerToken) {
        validateTokenWithAPI(token).then((isAdmin) => {
          if (isAdmin === null) {
            setBearerToken("");
            setIsAdmin(false);
            setIsAuthenticated(false);
          } else if (isAdmin !== undefined) {
            setBearerToken(token);
            setIsAdmin(isAdmin);
            setIsAuthenticated(!!isAdmin);
          }
          // undefined = network error: retry on next poll
        });
      }
    };

    // Check immediately
    checkTokenStatus();

    // Listen for storage events (when localStorage changes in other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "token" || e.key === "is_admin") {
        checkTokenStatus();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Poll for same-tab localStorage changes (storage event doesn't fire for same-tab edits)
    const pollInterval = setInterval(() => {
      const { token } = retrieveTokens();
      if (token !== bearerToken) {
        checkTokenStatus();
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [bearerToken]);

  // Additional effect to handle logout when token becomes empty
  useEffect(() => {
    if (!bearerToken || bearerToken === "" || bearerToken === '""') {
      navigate("/login");
    }
  }, [bearerToken, navigate]);

  const login = async (creadentials) => {
    let token;
    setUsedUsername(creadentials.username);

    try {
      token = await loginFunc(creadentials);
      setIsAdmin(token.is_admin);
      setIsAuthenticated(true);
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        if (err.response.status === 401) {
          notification.error({
            message: err.response.data.detail,
          });
        } else {
          notification.error({
            message: err.response.status,
          });
        }
      }
    }
    setBearerToken(token.access_token);
  };

  const logout = () => {
    logoutFunc(); // Use standardized logout function
    setBearerToken("");
    setIsAdmin(false);
    setIsAuthenticated(false);
  };

  if (isAuthenticated) {
    configureClient(process.env.REACT_APP_CALENDER_API);
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAdmin,
        bearerToken,
        login,
        logout,
        handleApiLogout,
        validateTokenWithAPI,
        forceValidateToken: () => {
          const { token } = retrieveTokens();
          if (token && token !== '""' && token !== null && token !== "") {
            console.log("Force validating current token...");
            return validateTokenWithAPI(token);
          }
          return Promise.resolve(false);
        },
        usedUsername,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};

export default AuthContextProvider;
